/**
 * API client for saved analyses.
 *
 * UI concerns live in saved-analyses-surface.js. This client deliberately
 * avoids native alert/prompt/confirm dialogs so callers can provide accessible
 * in-page feedback.
 */

class HistoryRequestError extends Error {
    constructor(message, status = 0, payload = null) {
        super(message);
        this.name = 'HistoryRequestError';
        this.status = status;
        this.payload = payload;
    }
}

class HistoryManager {
    constructor() {
        this.analyses = [];
        this.policy = null;
        this.pagination = null;
        this.listRequests = new Map();
        this.sharingRequest = null;
        this.openedAnalysisId = null;
        this.openedToolType = null;
        this.openedReadOnly = false;
        this.openIntentSequence = 0;
        this.currentOpenIntent = null;
        this.openApplyTail = Promise.resolve();
        this.versionRestoreTails = new Map();
    }

    async request(path, options = {}) {
        const response = await fetch(path, {
            credentials: 'include',
            ...options,
            headers: {
                ...(options.body ? { 'Content-Type': 'application/json' } : {}),
                ...(options.headers || {}),
            },
        });
        let payload = null;
        try {
            payload = await response.json();
        } catch (_error) {
            payload = null;
        }
        if (!response.ok) {
            const detail = payload?.detail;
            const message = (
                (typeof detail === 'object' && detail?.message)
                || (typeof detail === 'string' && detail)
                || payload?.error
                || `Erreur HTTP ${response.status}`
            );
            throw new HistoryRequestError(message, response.status, payload);
        }
        return payload || {};
    }

    async saveAnalysis(
        analysisName,
        toolType,
        zonesConfig,
        statsConfig,
        resultData,
        tags,
        notes,
        folder = '',
    ) {
        const result = await this.request('/api/history/save', {
            method: 'POST',
            body: JSON.stringify({
                analysis_name: analysisName,
                tool_type: toolType,
                zones_config: zonesConfig || {},
                stats_config: statsConfig || {},
                result_data: resultData || {},
                tags: tags || [],
                notes: notes || '',
                folder: folder || '',
            }),
        });
        return result.analysis_id ?? null;
    }

    async saveAnalysisPayload(payload) {
        return this.saveAnalysis(
            payload.analysis_name,
            payload.tool_type,
            payload.zones_config,
            payload.stats_config,
            payload.result_data,
            payload.tags,
            payload.notes,
            payload.folder,
        );
    }

    beginOpenIntent({
        analysisId = null,
        source = 'unknown',
        onSuperseded = null,
    } = {}) {
        const previous = this.currentOpenIntent;
        try {
            previous?.onSuperseded?.(previous.intent);
        } catch (_error) {
            // A stale status cleanup must never block the newer user intent.
        }
        previous?.controller?.abort();
        const controller = typeof AbortController === 'function'
            ? new AbortController()
            : null;
        const parsedAnalysisId = Number.parseInt(analysisId, 10);
        const intent = Object.freeze({
            sequence: ++this.openIntentSequence,
            analysisId: Number.isFinite(parsedAnalysisId) ? parsedAnalysisId : null,
            source: String(source || 'unknown'),
            signal: controller?.signal || null,
        });
        this.currentOpenIntent = {
            intent,
            controller,
            onSuperseded: typeof onSuperseded === 'function' ? onSuperseded : null,
        };
        return intent;
    }

    isOpenIntentCurrent(intent) {
        return Boolean(
            intent
            && this.currentOpenIntent?.intent === intent
            && Number(intent.sequence) === this.openIntentSequence
        );
    }

    assertOpenIntentCurrent(intent) {
        if (this.isOpenIntentCurrent(intent)) return intent;
        const error = new Error('Cette ouverture a été remplacée par une demande plus récente.');
        error.name = 'OpenIntentSupersededError';
        error.code = 'open_intent_superseded';
        throw error;
    }

    invalidateOpenIntent() {
        const previous = this.currentOpenIntent;
        try {
            previous?.onSuperseded?.(previous.intent);
        } catch (_error) {
            // The explicit user action still owns the cancellation.
        }
        previous?.controller?.abort();
        this.currentOpenIntent = null;
        this.openIntentSequence += 1;
    }

    async runOpenIntent(intent, callback) {
        const run = this.openApplyTail
            .catch(() => null)
            .then(async () => {
                if (!this.isOpenIntentCurrent(intent)) return null;
                const context = Object.freeze({
                    intent,
                    isCurrent: () => this.isOpenIntentCurrent(intent),
                    assertCurrent: () => this.assertOpenIntentCurrent(intent),
                });
                return callback(context);
            });
        this.openApplyTail = run.catch(() => null);
        return run;
    }

    async waitForPendingVersionRestores(analysisId) {
        const key = String(Number.parseInt(analysisId, 10));
        const pending = this.versionRestoreTails.get(key);
        if (pending) await pending;
    }

    async queueVersionRestore(analysisId, intent, callback) {
        const key = String(Number.parseInt(analysisId, 10));
        const previous = this.versionRestoreTails.get(key) || Promise.resolve();
        const run = previous
            .catch(() => null)
            .then(async () => {
                if (!this.isOpenIntentCurrent(intent)) return null;
                const context = Object.freeze({
                    intent,
                    isCurrent: () => this.isOpenIntentCurrent(intent),
                    assertCurrent: () => this.assertOpenIntentCurrent(intent),
                });
                return callback(context);
            });
        const tail = run.catch(() => null);
        this.versionRestoreTails.set(key, tail);
        void tail.finally(() => {
            if (this.versionRestoreTails.get(key) === tail) {
                this.versionRestoreTails.delete(key);
            }
        });
        return run;
    }

    openIntentAbortError() {
        const error = new Error('Cette ouverture a été remplacée par une demande plus récente.');
        error.name = 'AbortError';
        error.code = 'open_intent_superseded';
        return error;
    }

    async loadAnalysis(analysisId, { signal = null } = {}) {
        const result = await this.request(`/api/history/${analysisId}`, { signal });
        return result.analysis || null;
    }

    async loadAnalysisForOpen(analysisId, { signal = null } = {}) {
        await this.waitForPendingVersionRestores(analysisId);
        if (signal?.aborted) throw this.openIntentAbortError();
        return this.loadAnalysis(analysisId, { signal });
    }

    async listAnalyses(limit = 50, offset = 0, filters = {}) {
        const params = new URLSearchParams({
            limit: String(limit),
            offset: String(offset),
        });
        const query = String(filters.query || '').trim();
        const folder = String(filters.folder || '').trim();
        const toolType = String(filters.toolType || filters.tool_type || '').trim();
        const businessOrigin = String(filters.business_origin || '').trim();
        if (query) params.set('query', query);
        if (folder) params.set('folder', folder);
        if (toolType) params.set('tool_type', toolType);
        if (businessOrigin) params.set('business_origin', businessOrigin);
        const requestKey = params.toString();
        if (!this.listRequests.has(requestKey)) {
            const request = this.request(`/api/history/list?${requestKey}`)
                .then((result) => {
                    const analyses = Array.isArray(result.analyses) ? result.analyses : [];
                    const page = {
                        analyses,
                        count: Number(result.count ?? analyses.length),
                        total: Number(result.total ?? analyses.length),
                        has_more: Boolean(result.has_more),
                        next_offset: result.next_offset ?? null,
                        folders: Array.isArray(result.folders) ? result.folders : [],
                        query: result.query || query,
                        folder: result.folder || folder,
                        tool_type: result.tool_type || toolType,
                        policy: result.policy || null,
                    };
                    if (!offset && !query && !folder && !toolType) {
                        this.analyses = analyses;
                    }
                    this.policy = result.policy || null;
                    this.pagination = page;
                    return page;
                })
                .finally(() => {
                    this.listRequests.delete(requestKey);
                });
            this.listRequests.set(requestKey, request);
        }
        return this.listRequests.get(requestKey);
    }

    markOpenedAnalysis(analysisId, toolType, { readOnly = false } = {}) {
        const parsedId = Number.parseInt(analysisId, 10);
        this.openedAnalysisId = Number.isFinite(parsedId) ? parsedId : null;
        this.openedToolType = this.openedAnalysisId ? String(toolType || '') : null;
        this.openedReadOnly = Boolean(this.openedAnalysisId && readOnly);
    }

    clearOpenedAnalysis() {
        this.openedAnalysisId = null;
        this.openedToolType = null;
        this.openedReadOnly = false;
    }

    isOpenedAnalysis(analysisId, toolType) {
        return (
            !this.openedReadOnly
            && Number(this.openedAnalysisId) === Number(analysisId)
            && String(this.openedToolType || '') === String(toolType || '')
        );
    }

    isOpenedReadOnly() {
        return this.openedReadOnly;
    }

    async updateAnalysis(analysisId, updates) {
        const result = await this.request(`/api/history/${analysisId}`, {
            method: 'PATCH',
            body: JSON.stringify(updates || {}),
        });
        return result.analysis || null;
    }

    async replaceAnalysisSnapshot(analysisId, expectedSnapshotRevision, currentState) {
        const result = await this.request(`/api/history/${analysisId}/snapshot`, {
            method: 'PUT',
            body: JSON.stringify({
                expected_snapshot_revision: expectedSnapshotRevision,
                zones_config: currentState?.zones_config || {},
                stats_config: currentState?.stats_config || {},
                result_data: currentState?.result_data ?? null,
            }),
        });
        return result.analysis || null;
    }

    async moveAnalysisZones(analysisId, zoneIds, folder, expectedFolders) {
        const result = await this.request(`/api/history/${analysisId}`, {
            method: 'PATCH',
            body: JSON.stringify({
                zone_folder_move: {
                    zone_ids: zoneIds,
                    folder: folder || '',
                    expected_folders: expectedFolders || {},
                },
            }),
        });
        return result.zone_folder_move || null;
    }

    async updateAnalysisZoneMetadata(analysisId, expected, changes) {
        const result = await this.request(`/api/history/${analysisId}`, {
            method: 'PATCH',
            body: JSON.stringify({
                zone_metadata_update: {
                    expected: expected || {},
                    changes: changes || {},
                },
            }),
        });
        return result.zone_metadata_update || null;
    }

    async deleteAnalysis(analysisId) {
        const result = await this.request(`/api/history/${analysisId}`, {
            method: 'DELETE',
        });
        return Boolean(result.success);
    }

    async createVersion(analysisId, changesNotes = '', currentState = null) {
        const body = {
            changes_notes: changesNotes || '',
        };
        if (currentState) {
            body.zones_config = currentState.zones_config || {};
            body.stats_config = currentState.stats_config || {};
            body.result_data = currentState.result_data || {};
        }
        const result = await this.request(`/api/history/${analysisId}/version`, {
            method: 'POST',
            body: JSON.stringify(body),
        });
        return result.version_id ?? null;
    }

    async getVersions(analysisId) {
        const result = await this.request(`/api/history/${analysisId}/versions`);
        return Array.isArray(result.versions) ? result.versions : [];
    }

    async restoreVersion(analysisId, versionId) {
        const result = await this.request(`/api/history/${analysisId}/restore`, {
            method: 'POST',
            body: JSON.stringify({ version: versionId }),
        });
        return Boolean(result.success);
    }

    async compareVersions(analysisId, version1, version2) {
        const result = await this.request(`/api/history/${analysisId}/compare-versions`, {
            method: 'POST',
            body: JSON.stringify({ version1, version2 }),
        });
        return result.differences || null;
    }

    async compareSavedAnalyses(
        scenarios,
        criteriaWeights = null,
        comparisonMode = 'advanced',
    ) {
        const result = await this.request('/api/comparison/compare', {
            method: 'POST',
            body: JSON.stringify({
                scenarios,
                criteria_weights: criteriaWeights,
                comparison_mode: comparisonMode,
            }),
        });
        return result.snapshot || null;
    }

    async saveSavedComparison(
        comparisonName,
        scenarios,
        folder = '',
        criteriaWeights = null,
        comparisonMode = 'advanced',
    ) {
        const result = await this.request('/api/comparison/save', {
            method: 'POST',
            body: JSON.stringify({
                comparison_name: comparisonName,
                scenarios,
                folder,
                criteria_weights: criteriaWeights,
                comparison_mode: comparisonMode,
            }),
        });
        return {
            analysisId: result.analysis_id ?? null,
            snapshot: result.snapshot || null,
        };
    }

    async exportSavedComparison(
        snapshot,
        comparisonAnalysisId = null,
        scenarioReferences = null,
    ) {
        if (!snapshot?.comparison || !window.MaplyticsExportClient?.postJson) {
            throw new Error('Le module d’export comparatif est indisponible.');
        }
        const snapshotVersion = Number.parseInt(snapshot.snapshot_version, 10) || 1;
        const exportPayload = {
            async_generation: true,
            export_config: {
                snapshot_version: snapshotVersion,
            },
        };
        if (snapshotVersion >= 2) {
            Object.assign(
                exportPayload,
                this.savedComparisonCanonicalReference(
                    snapshot,
                    comparisonAnalysisId,
                    scenarioReferences,
                ),
            );
        } else {
            exportPayload.comparison_data = snapshot.comparison;
        }
        return window.MaplyticsExportClient.postJson(
            'comparison-pdf',
            '/api/export/comparison-pdf',
            exportPayload,
        );
    }

    savedComparisonCanonicalReference(
        snapshot,
        comparisonAnalysisId = null,
        scenarioReferences = null,
    ) {
        const snapshotVersion = Number.parseInt(snapshot?.snapshot_version, 10) || 1;
        if (snapshotVersion < 2 || !snapshot?.comparison) {
            throw new Error('Cette comparaison historique ne possède pas de snapshot exportable.');
        }
        const fingerprint = String(snapshot.dataset_fingerprint || '');
        if (!/^[a-f0-9]{32}$/.test(fingerprint)) {
            throw new Error('La référence du dataset comparatif est invalide.');
        }
        const reference = {expected_dataset_fingerprint: fingerprint};
        const savedAnalysisId = Number.parseInt(comparisonAnalysisId, 10);
        if (Number.isInteger(savedAnalysisId) && savedAnalysisId > 0) {
            reference.comparison_analysis_id = savedAnalysisId;
        } else if (
            Array.isArray(scenarioReferences)
            && scenarioReferences.length >= 2
            && scenarioReferences.length <= 5
        ) {
            reference.scenarios = scenarioReferences;
            reference.comparison_mode = snapshot.comparison_mode || 'advanced';
            if (reference.comparison_mode === 'advanced') {
                reference.criteria_weights = snapshot.criteria_weights || null;
            }
        } else {
            throw new Error('Les références de la comparaison sont incomplètes.');
        }
        return reference;
    }

    async exportSavedComparisonWorkbook(
        snapshot,
        comparisonAnalysisId = null,
        scenarioReferences = null,
        workbookConfig = null,
    ) {
        if (!window.MaplyticsExportClient?.postJson) {
            throw new Error('Le module de téléchargement est indisponible.');
        }
        const payload = {
            ...this.savedComparisonCanonicalReference(
                snapshot,
                comparisonAnalysisId,
                scenarioReferences,
            ),
            workbook_config: workbookConfig,
        };
        return window.MaplyticsExportClient.postJson(
            'comparison-excel',
            '/api/export/comparison-excel',
            payload,
        );
    }

    async searchAnalyses(query, tags, dateFrom, dateTo, { limit = 50, offset = 0, folder = '' } = {}) {
        const body = {};
        if (query) body.query = query;
        if (tags?.length) body.tags = tags;
        if (dateFrom) body.date_from = dateFrom;
        if (dateTo) body.date_to = dateTo;
        if (folder) body.folder = folder;
        body.limit = limit;
        body.offset = offset;
        const result = await this.request('/api/history/search', {
            method: 'POST',
            body: JSON.stringify(body),
        });
        return Array.isArray(result.results) ? result.results : [];
    }

    async listShares() {
        if (!this.sharingRequest) {
            this.sharingRequest = this.request('/api/history/shared')
                .then((result) => ({
                    received: Array.isArray(result.received)
                        ? result.received
                        : (Array.isArray(result.shared_analyses) ? result.shared_analyses : []),
                }))
                .finally(() => {
                    this.sharingRequest = null;
                });
        }
        return this.sharingRequest;
    }

    async shareAnalysisByEmail(analysisId, recipientEmail) {
        const result = await this.request(`/api/history/${analysisId}/share`, {
            method: 'POST',
            body: JSON.stringify({
                recipient_email: recipientEmail,
                permission: 'read',
            }),
        });
        return result.message || 'Demande de partage enregistrée.';
    }

    async shareAnalysis(analysisId, sharedWithUserId, permission = 'read') {
        const result = await this.request(`/api/history/${analysisId}/share`, {
            method: 'POST',
            body: JSON.stringify({
                shared_with_user_id: sharedWithUserId,
                permission,
            }),
        });
        return result.share_id ?? null;
    }

    async revokeShare(analysisId, shareId) {
        const result = await this.request(
            `/api/history/${analysisId}/share/${shareId}`,
            { method: 'DELETE' },
        );
        return Boolean(result.success);
    }

    async revokeSharesByEmail(recipientEmail) {
        const result = await this.request('/api/history/shares/by-email', {
            method: 'DELETE',
            body: JSON.stringify({
                recipient_email: recipientEmail,
            }),
        });
        return result.message || 'Demande de révocation enregistrée.';
    }

    async createTemplate(
        templateName,
        toolType,
        zonesConfig,
        statsConfig,
        isPublic,
        sector,
    ) {
        const result = await this.request('/api/history/template', {
            method: 'POST',
            body: JSON.stringify({
                template_name: templateName,
                tool_type: toolType,
                zones_config: zonesConfig || {},
                stats_config: statsConfig || {},
                is_public: Boolean(isPublic),
                sector: sector || null,
            }),
        });
        return result.template_id ?? null;
    }
}

window.HistoryManager = HistoryManager;
window.HistoryRequestError = HistoryRequestError;
